import type { PortableTextBlock } from "@portabletext/react";
import { stringify } from "qs";
import { type Nominal, type TypedUrl, url } from "./common";
import type { WorkshopSettings } from "./workshop";

// ---- IDs ----

export type SparkId = Nominal<string, "Spark">;
type SparkModuleId = Nominal<string, "SparkModule">;
type SanityBlockContentId = Nominal<string, "SanityBlockContent">;

// ---- Sub-shapes ----

export type ProjectFormField = {
  key: string;
  label: string;
  description: string;
  type: "singleLine" | "multiLine" | "image" | "projectUpload";
  // Sanity sends `null` for a field that has no rules.
  rules:
    | { type: "required" | "minLength" | "maxLength"; value?: number }[]
    | null;
};

// Sanity block content: Portable Text, an array of blocks.
export type SanityBlockContent = PortableTextBlock[];

// ---- Schedule (Spark.structure) ----

type Station = {
  key: string;
  title: string;
  type: "station";
  recommendedMinutes: number;
  disabled: boolean;
  stageSlug: string;
  steps: SparkModule[];
};

export type StationOrProcedure = SparkModule | Station;

export type Stage = {
  name: string;
  slug: string;
  disabled: boolean;
  modules: StationOrProcedure[];
};

export type Phase = {
  name: string;
  type: "discover" | "create" | "share" | "custom";
  disabled: boolean;
  stages: Stage[];
};

export type Schedule = Phase[];

// ---- Entity ----

// The Sanity module's remaining fields, as passed through by the import. Block
// content fields arrive as `<name>Key` references (e.g. `contentKey`). Only
// steps inside a station carry `station`.
type SparkModuleContent = {
  station?: { _key: string; disabled: boolean; title: string };
  typeTitle?: string | null;
  typeDarkImage?: string | null;
  contentKey?: SanityBlockContentId;
  [field: string]: unknown;
};

export type SparkModule = {
  key: SparkModuleId;
  sparkId: SparkId;
  stageSlug: string;
  orderIdx: number;
  disabled: boolean;
  type: "procedure" | "step";
  recommendedMinutes: number;
  slug: string;
  title: string;
  previewImageUrl: string | null;
  sparkStateQuery: boolean;
  content: SparkModuleContent;
  archivedAt: Date | null;
  createdAt: Date;
};

export type Spark = {
  id: SparkId;
  name: string;
  inAppTitle: string;
  slug: string;
  format: string;
  imageUrl: string | null;
  summaryKey: SanityBlockContentId;
  descriptionKey: SanityBlockContentId;
  createdAt: Date;
  tags: string[];
  flags: string[];
  projectFormDefinition: {
    title: string;
    fields: ProjectFormField[];
  };
  availableForTeachers: boolean;
  color: string | null;
  templateWorkshopSlug: string | null;
  time: string | null;
  pendantSparks: SparkId[];
  partner: string | null;
  structure: Schedule;
  showGlossary: boolean;
};

// ---- Glossary ----

// Map of canonical term to its aliases, returned by the terms endpoint.
// Aliases highlight too but resolve back to the canonical term.
export type GlossaryTerms = Record<string, string[]>;

// Short form returned by the descriptions endpoint (no long description).
export type GlossaryDescription = {
  term: string;
  description: string;
};

// Full entry returned by the glossary endpoint, including the long description
// (Portable Text, null when unset).
export type GlossaryEntry = GlossaryDescription & {
  longDescription: SanityBlockContent | null;
};

// ---- Response shapes ----

// Returned by GET /sparks — subset of Spark fields plus the templateWorkshopSlug
// filter and the resolved `summary` (the endpoint batches the summaryKey lookup,
// so list consumers never fetch the block content themselves).
export type SparkListItem = Pick<
  Spark,
  | "id"
  | "slug"
  | "inAppTitle"
  | "color"
  | "summaryKey"
  | "partner"
  | "time"
  | "tags"
  | "createdAt"
  | "availableForTeachers"
  | "templateWorkshopSlug"
> & {
  summary: SanityBlockContent | null;
};

// Returned by GET /spark when withDefaultSettings=true (and a templateWorkshopSlug exists).
export type SparkWithDefaultSettings = Spark & {
  defaultSettings: WorkshopSettings;
};

// Permissive shape used by populated `spark` relations on workshop endpoints.
// The by-id (GET /workshop/:id) and list (GET /me/workshops) projections return
// overlapping but distinct subsets of Spark plus a computed `summary` field —
// this shape accommodates both. The by-slug projection returns the richer
// `WorkshopStepSpark` below.
export type SparkSummary = {
  id: SparkId;
  inAppTitle: string;
  name?: string;
  slug?: string;
  color?: string | null;
  tags?: string[] | null;
  partner?: string | null;
  time?: string | null;
  flags?: string[] | null;
  summaryKey?: SanityBlockContentId;
  summary?: SanityBlockContent;
  projectFormDefinition?: {
    title: string;
    fields: ProjectFormField[];
  };
};

// Spark relation on the workshop-by-slug projection (participant step view):
// adds the feature flags and full schedule that the step UI needs.
export type WorkshopStepSpark = SparkSummary & {
  slug: string;
  showGlossary: boolean;
  structure: Schedule;
};

// ---- Models ----

export const sparkModels = {
  SPARKS: (includeNotAvailableForTeachers = false) =>
    url<SparkListItem[]>(
      `/sparks${stringify({ includeNotAvailableForTeachers }, { addQueryPrefix: true })}`,
    ),

  // Two return shapes depending on `withDefaultSettings` — overloads pick the precise one.
  SPARK: ((opts: { slug: string; withDefaultSettings?: boolean }) =>
    url<Spark | SparkWithDefaultSettings>(
      `/spark${stringify(opts, { addQueryPrefix: true })}`,
    )) as {
    (opts: {
      slug: string;
      withDefaultSettings: true;
    }): TypedUrl<SparkWithDefaultSettings>;
    (opts: { slug: string; withDefaultSettings?: false }): TypedUrl<Spark>;
  },

  SPARK_SCHEDULE_STEP: (sparkSlug: string, stepSlug: string) =>
    url<SparkModule>(`/spark/${sparkSlug}/step/${stepSlug}`),
  SPARK_SCHEDULE_STEPS: (
    sparkSlug: string,
    stageSlug: string | null = null,
    limit: number | null = null,
  ) =>
    url<SparkModule[]>(
      `/spark/${sparkSlug}/steps${stringify({ stageSlug, limit }, { addQueryPrefix: true })}`,
    ),
  SPARK_SCHEDULE_STATIONS_AND_PROCEDURES: (
    sparkSlug: string,
    stageSlug: string,
  ) =>
    url<StationOrProcedure[]>(
      `/spark/${sparkSlug}/stage/${stageSlug}/stations-procedures`,
    ),

  // Returns just `.content` from the SanityBlockContent record — Portable Text blob.
  SPARK_MODULE_CONTENT: (key: string) =>
    url<SanityBlockContent>(`/spark-module-content/${key}`),

  // All glossary terms for a spark — a map of canonical term to its aliases
  // (e.g. {"Preußen": ["Preußens", "preußische"], "Reich": []}). Used to detect
  // and highlight terms while rendering step content; aliases resolve back to
  // the canonical term.
  SPARK_GLOSSARY_TERMS: (sparkSlug: string) =>
    url<GlossaryTerms>(`/spark/${sparkSlug}/glossary/terms`),

  // Short descriptions for a given set of terms (no long descriptions). Terms
  // are passed comma-separated and individually percent-encoded.
  SPARK_GLOSSARY_DESCRIPTIONS: (sparkSlug: string, terms: string[]) =>
    url<GlossaryDescription[]>(
      `/spark/${sparkSlug}/glossary/descriptions?terms=${terms
        .map(encodeURIComponent)
        .join(",")}`,
    ),

  // Full glossary for a spark, including long descriptions.
  SPARK_GLOSSARY: (sparkSlug: string) =>
    url<GlossaryEntry[]>(`/spark/${sparkSlug}/glossary`),
};
